/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useRef } from 'react';

import { css, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const loadingDotWaveWrapperStyles = css({
	position: 'absolute',
	top: token('space.negative.200'),
});

interface LoadingDotWaveProps {
	width: number;
	height?: number;
}

interface Dot {
	x: number;
	y: number;
	baseY: number;
	phase: number;
	size: number;
}

export const LoadingDotWave = ({ width, height = 15 }: LoadingDotWaveProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const dotsRef = useRef<Dot[]>([]);
	const animationRef = useRef<number>();
	const timeRef = useRef(0);

	// Calculate number of dots based on consistent spacing
	const dotRadius = 2;
	const idealSpacing = width < 18 ? 6 : 8.4;
	const availableWidth = width - dotRadius * 2;
	const maxDots = Math.floor(availableWidth / idealSpacing) + 1;
	const dotCount = Math.max(2, maxDots);

	// Initialize dots
	const initDots = useCallback(() => {
		const dots: Dot[] = [];
		const centerY = height / 2;

		// Calculate available space for dots (subtract padding for dot radius on both sides)
		const availableWidth = width - 1 - dotRadius * 2;
		const spacing = dotCount > 1 ? availableWidth / (dotCount - 1) : 0;

		for (let i = 0; i < dotCount; i++) {
			dots.push({
				x: dotRadius + i * spacing, // Start from dotRadius to avoid cutoff
				y: centerY,
				baseY: centerY,
				phase: (i / dotCount) * Math.PI * 2,
				size: dotRadius,
			});
		}

		return dots;
	}, [dotCount, height, width]);

	// Gradient for dots
	const createGradient = useCallback(
		(ctx: CanvasRenderingContext2D, x: number) => {
			const gradient = ctx.createRadialGradient(x, 0, 0, x, 0, dotRadius);

			// Calculate position along the available width for color interpolation
			const availableWidth = width - dotRadius * 2;
			const adjustedX = x - dotRadius;
			const position =
				availableWidth > 0 ? Math.max(0, Math.min(1, adjustedX / availableWidth)) : 0;

			// Create linear gradient with colors: #0065FF → #0267FF → #BF63F3 → #FFA900
			const colors = [
				{ r: 0, g: 101, b: 255 }, // #0065FF
				{ r: 2, g: 103, b: 255 }, // #0267FF
				{ r: 191, g: 99, b: 243 }, // #BF63F3
				{ r: 255, g: 169, b: 0 }, // #FFA900
			];

			let color;
			if (position <= 0.33) {
				// Interpolate between #0065FF and #0267FF
				const localPos = position / 0.33;
				const color1 = colors[0];
				const color2 = colors[1];
				color = {
					r: Math.floor(color1.r + (color2.r - color1.r) * localPos),
					g: Math.floor(color1.g + (color2.g - color1.g) * localPos),
					b: Math.floor(color1.b + (color2.b - color1.b) * localPos),
				};
			} else if (position <= 0.66) {
				// Interpolate between #0267FF and #BF63F3
				const localPos = (position - 0.33) / 0.33;
				const color1 = colors[1];
				const color2 = colors[2];
				color = {
					r: Math.floor(color1.r + (color2.r - color1.r) * localPos),
					g: Math.floor(color1.g + (color2.g - color1.g) * localPos),
					b: Math.floor(color1.b + (color2.b - color1.b) * localPos),
				};
			} else {
				// Interpolate between #BF63F3 and #FFA900
				const localPos = (position - 0.66) / 0.34;
				const color1 = colors[2];
				const color2 = colors[3];
				color = {
					r: Math.floor(color1.r + (color2.r - color1.r) * localPos),
					g: Math.floor(color1.g + (color2.g - color1.g) * localPos),
					b: Math.floor(color1.b + (color2.b - color1.b) * localPos),
				};
			}

			gradient.addColorStop(0, `rgb(${color.r}, ${color.g}, ${color.b})`);
			gradient.addColorStop(
				1,
				`rgb(${Math.floor(color.r * 0.8)}, ${Math.floor(color.g * 0.8)}, ${Math.floor(color.b * 0.8)})`,
			);

			return gradient;
		},
		[width],
	);

	// Animation loop
	const animate = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			return;
		}

		// Clear canvas
		ctx.clearRect(0, 0, canvas.width, canvas.height);

		// Update time
		timeRef.current += 0.08;

		// Update and draw dots
		dotsRef.current.forEach((dot) => {
			// Calculate wave motion
			const waveOffset = Math.sin(timeRef.current + dot.phase);
			dot.y = dot.baseY + waveOffset;

			// Apply gradient to this dot
			const gradient = createGradient(ctx, dot.x);

			// Draw dot
			ctx.beginPath();
			ctx.arc(dot.x, dot.y, dot.size, 0, Math.PI * 2);
			ctx.fillStyle = gradient;
			ctx.fill();

			// Add subtle shadow
			ctx.shadowColor = 'rgba(0, 0, 0, 0.1)';
			ctx.shadowBlur = 2;
			ctx.shadowOffsetY = 1;
		});

		// Continue animation
		animationRef.current = requestAnimationFrame(animate);
	}, [canvasRef, createGradient]);

	// Initialize dots when width changes
	useEffect(() => {
		dotsRef.current = initDots();
	}, [width, dotCount, height, initDots]);

	// Set up canvas and start animation
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) {
			return;
		}

		// Set canvas dimensions
		canvas.width = width;
		canvas.height = height;

		// Start animation
		animationRef.current = requestAnimationFrame(animate);

		// Cleanup
		return () => {
			if (animationRef.current) {
				cancelAnimationFrame(animationRef.current);
			}
		};
	}, [animate, height, width]);

	return (
		<div css={loadingDotWaveWrapperStyles}>
			<canvas ref={canvasRef} width={width} height={height} />
		</div>
	);
};
